import { defineStore } from "pinia";
import { computed, ref } from "vue";
import ApiUsers from "@/apis/ApiUsers";
import { useAppsStore } from "./appsStore";
import { useCompaniesStore } from "./companiesStore";
import { catchable } from "./piniaUtils";

export const READONLY = "read_only";
export const ADMIN = "admin";
export const SUPER_ADMIN = "super_admin";
export const CORP_IT = "corp_it";
export const MEMBER = "member";

export const APP_ROLES = { [MEMBER]: "Member", [ADMIN]: "Admin", [READONLY]: "Read only" };

export const useUserStore = defineStore("user", () => {
  const appsStore = useAppsStore();
  const companiesStore = useCompaniesStore();
  const currUser = ref({ apps: [], role: READONLY });

  const isSales = computed(() => currUser.value?.departments?.includes("sales"));
  const currAppUserRole = computed(() =>
    currUser.value.apps.find(({ id }) => id === appsStore.app.id),
  );

  // Current role computed properties that consider company context
  const currentRole = computed(() => currUser.value.current_role || currUser.value.role);
  const isCurrentAdmin = computed(() => currentRole.value === ADMIN);
  const isCurrentSuperAdmin = computed(() => currentRole.value === SUPER_ADMIN);
  const isCurrentMember = computed(() => currentRole.value === MEMBER);

  // Base role computed properties (from user's own company)
  const isAppAdmin = computed(() => currAppUserRole.value?.role === ADMIN);
  const isUserSuperAdmin = computed(() => currUser.value.role === SUPER_ADMIN);
  const isUserCorpIt = computed(() => currUser.value.role === CORP_IT);
  const isUserAdmin = computed(() => currUser.value.role === ADMIN);

  // Permissions computed properties that consider current role
  const canUpdateCompany = computed(() => [SUPER_ADMIN, ADMIN].includes(currentRole.value));
  const isAdmin = computed(
    () => isCurrentSuperAdmin.value || isCurrentAdmin.value || isAppAdmin.value,
  );
  const isReadOnly = computed(() => {
    if (!currUser.value?.apps || !appsStore.app?.id) return false;
    if (isAdmin.value) return false;
    return currAppUserRole.value?.role === READONLY;
  });

  const isDisabledWithMessage = computed(() => isReadOnly.value && "Insufficient Access");
  const isDisabledExtendedWithMessage = computed(() => !isAdmin.value && "Insufficient Access");

  const mutateUpdateUserAuth = user => {
    currUser.value = user || (user && user.length) ? user : { id_token: "" };
    localStorage.setItem("id_token", currUser.value.id_token);
  };

  const updateUserSettings = user =>
    catchable({ t: async () => (currUser.value = await ApiUsers.updateUserSettings({ user })) });
  const updateCurrUser = user =>
    catchable({ t: async () => (currUser.value = await ApiUsers.updateUser({ user })) });
  const register = params => catchable({ t: () => ApiUsers.register(params) });
  const login = async ({ email, password }) => {
    // Clear any stale company context before login
    companiesStore.clearViewingCompany();
    return mutateUpdateUserAuth(await ApiUsers.loginUser({ email, password }));
  };

  const getUser = () =>
    catchable({
      t: async () => {
        const userIn = await ApiUsers.getUser();
        if (userIn) currUser.value = userIn;
      },
    });
  const resetPasswordUser = ({ reset_password_token, password, password_confirmation }) =>
    catchable({
      t: () =>
        ApiUsers.resetPasswordUser({ reset_password_token, password, password_confirmation }),
    });

  const canModifyUser = userRole =>
    isCurrentSuperAdmin.value || isCurrentAdmin.value || (isAppAdmin.value && userRole === MEMBER);

  const logout = ({ redirect = "/login" } = {}) =>
    catchable({
      t: async () => {
        await ApiUsers.logoutUser();
        localStorage.removeItem("id_token");
        companiesStore.clearViewingCompany();
        redirect && (location.href = redirect);
      },
    });

  const confirmInvitedUser = ({ invitation_token, password, password_confirmation }) =>
    catchable({
      t: async () =>
        await ApiUsers.confirmInvitedUser({ invitation_token, password, password_confirmation }),
    });

  return {
    currUser,
    register,
    login,
    getUser,
    resetPasswordUser,
    canUpdateCompany,
    currAppUserRole,
    isAdmin,
    isAppAdmin,
    isDisabledExtendedWithMessage,
    isDisabledWithMessage,
    isReadOnly,
    isSales,
    isUserAdmin,
    isUserSuperAdmin,
    isUserCorpIt,
    currentRole,
    isCurrentAdmin,
    isCurrentSuperAdmin,
    isCurrentMember,
    updateCurrUser,
    canModifyUser,
    logout,
    confirmInvitedUser,
    updateUserSettings,
  };
});
